import { LitElement, html, css } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';

import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import {
  formatDate,
  formatTime,
  converStatusToReadable,
  PickupOrderStatusManualActionRequired,
  PickupOrderStatusLoadingDone,
  PickupOrderStatusPackageReceiptCreated,
  PickupOrderStatusPackageReceiptBeingSend,
  PickupOrderStatusPackageReceiptSent,
  converBookingStatusToReadable,
  convertEmailStatusToReadable,
  BookingStatusBeingChecked,
  BookingStatusInProgress,
  BookingStatusPending,
  BookingStatusCompleted,
  PickupOrderStatusBooked,
} from '../../util/util';
import { showNotification, confirmPopup } from '../../LanghoutBetonPortal';
import {
  PickupOrderInvoice,
  Product,
  PickupOrderEmail,
} from '../../types/types';

import '../../elements/langhout-button/langhout-button';
import {
  closeIcon,
  playCircle,
  pauseCircle,
  alertCircle,
  checkCircle,
} from '../../elements/icons/icons';
import { sharedStyles } from '../../elements/shared-styles';

@customElement('langhout-beton-view-pickup-order-invoice')
export class ViewPickupOrderInvoice extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        font-size: 17px;
        color: var(--text-color-main);
        height: calc(100vh - 84px);
        max-height: calc(100vh - 84px);
        overflow-y: auto;
        background-color: white;
        z-index: 100;
        padding: 10px;
        padding-left: 30px;
        padding-right: 30px;
      }

      .row {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
      }

      .centered {
        justify-content: center;
      }

      .inner-wrapper {
        max-width: 600px;
        flex-grow: 1;
      }

      .centered-column {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .wrapper {
        padding-bottom: 400px;
        display: flex;
        flex-direction: row;
        justify-content: center;
      }

      .close-wrapper {
        padding: 5px;
      }

      .label {
        text-align: left;
        min-width: 180px;
        font-weight: bold;
        flex-grow: 1;
        max-width: 300px;
      }

      .value {
        text-align: right;
        min-width: 100px;
        flex-grow: 1;
        max-width: 300px;
      }

      .row-inner-wrapper {
        display: flex;
        flex-direction: row;
      }

      .prod-row {
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .issue {
        color: red;
      }

      .prod-amount {
        text-align: left;
        width: 180px;
        font-weight: bold;
      }

      .prod-name {
        text-align: left;
        min-width: 180px;
        max-width: 250px;
      }

      langhout-button {
        width: 250px;
        min-width: 250px;
        margin-right: 20px;
        margin-top: 20px;
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .button-row {
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .sub-order-wrapper {
        padding: 10px;
        border: 2px solid var(--primary-color);
        margin-top: 15px;
        border-radius: 5px;
      }

      svg {
        margin-top: 24.4px;
        margin-right: 10px;
      }

      .delivery-note-number {
        margin-bottom: 15px;
      }

      .comment-wrapper {
        padding: 10px;
        border: 2px solid var(--primary-color);
        margin-top: 15px;
        border-radius: 5px;
      }

      .add-comment-wrapper {
        margin-top: 20px;
        display: flex;
        flex-direction: row;
      }

      #comment {
        min-width: 200px;
      }

      .comment-input-wrapper {
        margin-top: 20px;
      }

      .comment-space {
        width: 40px;
      }

      .comment-message {
        text-align: left;
      }

      .comment-user-name,
      .comment-time {
        font-weight: bold;
        color: var(--primary-color);
      }
    `,
  ];

  @property({ type: Object })
  public currentInvoiceView: PickupOrderInvoice = <PickupOrderInvoice>{};

  @property({ type: Object, reflect: true })
  public currentEmailView: PickupOrderEmail[] = <PickupOrderEmail[]>[];

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @query('#comment')
  public commentInput?: HTMLInputElement;

  constructor() {
    super();
    document.addEventListener('confirm_booking_retry', this.retryBooking);
    document.addEventListener(
      'confirm_finished_pickup_order_delete',
      this.executeDeleteFinishedPickupOrder as EventListener,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('confirm_booking_retry', this.retryBooking);
    document.removeEventListener(
      'confirm_finished_pickup_order_delete',
      this.executeDeleteFinishedPickupOrder as EventListener,
    );
  }

  executeDeleteFinishedPickupOrder = async () => {
    this.scrollTo(0, 0);
    const result = await authenticatedAPICall(
      `/pickuporders/${this.currentInvoiceView.id}`,
      'DELETE',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      this.dispatchCloseEvent();
    }
  };

  render() {
    return html`
      <div class="row">
        <div class="fill"></div>
        <div
          class="close-wrapper"
          @click="${() => {
            this.dispatchCloseEvent();
          }}"
        >
          ${closeIcon()}
        </div>
      </div>
      <div class="wrapper">
        <div class="inner-wrapper">
          <div class="row centered">
            <h2>Pickup order</h2>
          </div>
          <div class="row centered display-props">
            <div class="label">Status:</div>
            <div class="value">
              ${converStatusToReadable(this.currentInvoiceView.status)}
            </div>
          </div>
          <div class="row centered display-props">
            <div class="label">Laaddatum:</div>
            <div class="value">
              ${formatDate(this.currentInvoiceView.pickup_date)}
            </div>
          </div>
          ${this.renderInitUser(this.currentInvoiceView)}
          ${this.renderReadyForPickupTime(this.currentInvoiceView)}
          ${this.renderLoadingUser(this.currentInvoiceView)}
          ${this.renderBeingLoadedTime(this.currentInvoiceView)}
          ${this.renderCleanupUser(this.currentInvoiceView)}
          ${this.renderCleanupTime(this.currentInvoiceView)}
          ${this.renderTransporter(this.currentInvoiceView)}
          ${this.renderLicensePlate(this.currentInvoiceView)}
          ${this.renderEmails(this.currentInvoiceView)}
          ${this.renderSubOrders()}
          ${this.renderButtons(this.currentInvoiceView)}
          ${this.renderDownloadButton()} ${this.renderDeleteButton()}
          ${this.renderFinishedOrder()} ${this.renderComments()}
          <div class="add-comment-wrapper button-row centered">
            <div class="centered-column comment-input-wrapper">
              <input
                type="text"
                placeholder="Opmerking"
                id="comment"
                name="comment"
                @keypress=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    this.addComment();
                  }
                }}
              />
            </div>
            <div class="comment-space"></div>
            <langhout-button
              id="commentButton"
              text="Opmerking plaatsen"
              @click="${() => {
                const message = this.commentInput
                  ? this.commentInput.value
                  : '';
                if (message === '') {
                  showNotification('Opmerking kan niet leeg zijn');
                  return;
                }
                this.addComment();
              }}"
            >
            </langhout-button>
          </div>
        </div>
      </div>
    `;
  }

  renderDeleteButton = () => {
    if (this.currentInvoiceView.status == PickupOrderStatusPackageReceiptSent) {
      return html`
        <div class="row centered">
          <langhout-button
            text="Verwijderen"
            removeButton
            @click="${async () => {
              confirmPopup({
                confirm_event: 'confirm_finished_pickup_order_delete',
                title: 'Order verwijderen',
                description:
                  'Als de levering wordt verwijderd dienen de volgende acties te worden ondernomen:<br><ul><li>De klant dient geinformeerd te worden dat de levering is vervallen en de verzonden pakbon ongeldig is.</li><li>De geleverde artikelen van deze Pickup Order dienen handmatig te worden teruggeboekt in Exact.</li></ul>',
                with_checkbox: true,
                checkbox_text:
                  'Ik heb de klant geinformeerd en de Pickup Order handmatig teruggeboekt in Exact.',
              });
            }}"
          ></langhout-button>
        </div>
      `;
    }
    return html``;
  };

  renderComments = () => {
    if (
      !this.currentInvoiceView.comments ||
      this.currentInvoiceView.comments.length === 0
    ) {
      return html``;
    }
    return html`
      <div class="row centered">
        <h2>Opmerkingen</h2>
      </div>
      ${this.currentInvoiceView.comments.map(comment => {
        return html`
          <div class="comment-wrapper">
            <div class="row">
              <div class="comment-user-name">${comment.user_name}</div>
              <div class="fill"></div>
              <div class="comment-time">
                ${formatTime(comment.timestamp, false)}
              </div>
            </div>
            <div class="row comment-message">${comment.message}</div>
          </div>
        `;
      })}
    `;
  };

  renderEmails(pickupOrder: PickupOrderInvoice) {
    if (
      pickupOrder.status !== PickupOrderStatusPackageReceiptBeingSend &&
      pickupOrder.status !== PickupOrderStatusPackageReceiptSent
    ) {
      return html``;
    }
    return html`
      <div class="row centered">
        <h2>Verzonden emails</h2>
      </div>
      ${this.currentInvoiceView.emails.map(email => {
        return html`
          <div class="sub-order-wrapper">
            <div class="row centered display-props">
              <div class="label">Verzonden aan:</div>
              <div class="value">${email.to.join(', ')}</div>
            </div>
            <div class="row centered display-props">
              <div class="label">Verzend status:</div>
              <div class="value">
                ${convertEmailStatusToReadable(email.status)}
              </div>
            </div>
          </div>
        `;
      })}
    `;
  }

  renderSubOrders() {
    if (!this.currentInvoiceView.sub_orders) {
      return html``;
    }
    if (
      this.currentInvoiceView.status != PickupOrderStatusBooked &&
      this.currentInvoiceView.status !=
        PickupOrderStatusPackageReceiptCreated &&
      this.currentInvoiceView.status !=
        PickupOrderStatusPackageReceiptBeingSend &&
      this.currentInvoiceView.status != PickupOrderStatusPackageReceiptSent
    ) {
      return html``;
    }
    return html`
      ${this.currentInvoiceView.sub_orders.map(subOrder => {
        return html`
          <div class="sub-order-wrapper">
            <div class="row">
              <div>
                ${this.renderBookingStatusIcon(subOrder.booking_status)}
              </div>
              <h3>${converBookingStatusToReadable(subOrder.booking_status)}</h3>
            </div>
            <div class="row centered display-props">
              <div class="label">Ordernummer LH:</div>
              <div class="value">${subOrder.order_id}</div>
            </div>
            <div class="row centered display-props">
              <div class="label">Vracht ID:</div>
              <div class="value">${subOrder.shipping_id}</div>
            </div>
            <div class="row centered display-props">
              <div class="label">Ordernummer klant:</div>
              <div class="value">${subOrder.external_id}</div>
            </div>
            <div class="row centered display-props">
              <div class="label">Klantnaam:</div>
              <div class="value">${subOrder.customer_name}</div>
            </div>
            <div class="row centered display-props">
              <div class="label">Plaatsnaam:</div>
              <div class="value">${subOrder.shipping_city}</div>
            </div>
            <div class="row">
              <h3>Producten</h3>
            </div>
            ${subOrder.products.map(product => {
              return html`
                <div class="row centered display-props prod-row">
                  <div class="prod-amount">
                    ${product.amount} st.
                    ${this.getAmountText(
                      product.amount_packs,
                      product.product_type,
                    )}
                  </div>
                  <div class="prod-packs"></div>
                  <div class="prod-name">${product.description}</div>
                  <div class="fill"></div>
                  <div class="prod-weight">
                    <div>
                      ${this.getWeightText(product.amount, product.weight)}
                    </div>
                  </div>
                </div>
              `;
            })}
            <div class="row">
              <div>Totaal gewicht:</div>
              <div class="fill"></div>
              ${this.getWeightTotalText(subOrder.products)}
            </div>
          </div>
        `;
      })}
    `;
  }

  renderBookingStatusIcon(bookingStatus: number) {
    switch (bookingStatus) {
      case BookingStatusBeingChecked:
        return html`${playCircle()}`;
      case BookingStatusInProgress:
        return html`${playCircle()}`;
      case BookingStatusPending:
        return html`${pauseCircle()}`;
      case BookingStatusCompleted:
        return html`${checkCircle()}`;
      default:
        return html`${alertCircle()}`;
    }
  }

  renderFinishedOrder() {
    if (
      this.currentEmailView.length > 0 &&
      this.currentInvoiceView.status === PickupOrderStatusPackageReceiptCreated
    ) {
      return html`
        ${this.currentEmailView.map((emailView, index) => {
          return html`
            <div class="row centered">
              <h2>Email ${index + 1}</h2>
            </div>
            ${this.renderEmailOrderViewIssue(emailView)}
            <div class="row centered display-props">
              <div class="label">Email naar:</div>
              <div class="value">${emailView.to}</div>
            </div>
            <div class="row centered display-props">
              <div class="label">Onderwerp:</div>
              <div class="value">${emailView.subject}</div>
            </div>
            <div class="row centered display-props">
              <div class="label">Content:</div>
              <div class="value">
                <iframe
                  src="data:text/html;charset=utf-8,${emailView.body}#navpanes=0"
                ></iframe>
              </div>
            </div>
            <div class="row centered">
              <h3>PDFs email ${index + 1}</h3>
            </div>
            ${emailView.pdfs.map(pdf => {
              if (pdf.data === '') {
                return html``;
              }
              return html`
                <div class="sub-order-wrapper">
                  <iframe
                    style="width: 100%; height: 800px;"
                    src="data:application/pdf;base64,${pdf.data}#navpanes=0"
                  ></iframe>
                </div>
              `;
            })}
          `;
        })}
      `;
    }

    if (!this.currentInvoiceView.sub_orders) {
      return html``;
    }
    return html`
      ${this.currentInvoiceView.sub_orders.map(subOrder => {
        if (!subOrder || !subOrder.pdf || subOrder.pdf === '') {
          return html``;
        }
        return html`
          <div class="sub-order-wrapper">
            <iframe
              style="width: 100%; height: 800px;"
              src="data:application/pdf;base64,${subOrder.pdf}#navpanes=0"
            ></iframe>
          </div>
        `;
      })}
    `;
  }

  renderEmailOrderViewIssue = (emailOrderView: PickupOrderEmail) => {
    if (emailOrderView.issue === '') {
      return html``;
    }

    return html`
      <div class="row centered display-props">
        <div class="label">Probleem:</div>
        <div class="value issue">${emailOrderView.issue}</div>
      </div>
    `;
  };

  renderDownloadButton = () => {
    if (
      this.currentInvoiceView.status == PickupOrderStatusPackageReceiptCreated
    ) {
      const text = 'PDF(s) downloaden';
      return html`
        <div class="row centered">
          <langhout-button
            text="${text}"
            @click="${() => {
              this.downloadAllEmailView(this.currentEmailView);
            }}"
          ></langhout-button>
        </div>
      `;
    }
    if (
      this.currentInvoiceView.status ==
        PickupOrderStatusPackageReceiptCreated ||
      this.currentInvoiceView.status ==
        PickupOrderStatusPackageReceiptBeingSend ||
      this.currentInvoiceView.status == PickupOrderStatusPackageReceiptSent
    ) {
      let text = 'PDF downloaden';
      if (
        this.currentInvoiceView &&
        this.currentInvoiceView.sub_orders &&
        this.currentInvoiceView.sub_orders.length > 1
      ) {
        text = 'PDFs downloaden';
      }
      return html`
        <div class="row centered">
          <langhout-button
            text="${text}"
            @click="${() => {
              this.downloadAllInvoiceView(this.currentInvoiceView);
            }}"
          ></langhout-button>
        </div>
      `;
    }
    return html``;
  };

  downloadAllInvoiceView = (pickupOrder: PickupOrderInvoice) => {
    for (let i = 0; i < pickupOrder.sub_orders.length; i++) {
      const subOrder = pickupOrder.sub_orders[i];
      const link = document.createElement('a');
      link.download = `${subOrder.file_name}`;
      link.href = 'data:application/octet-stream;base64,' + subOrder.pdf;
      link.click();
    }
  };

  downloadAllEmailView = (pickupOrderEmails: PickupOrderEmail[]) => {
    for (let i = 0; i < pickupOrderEmails.length; i++) {
      for (let j = 0; j < pickupOrderEmails[i].pdfs.length; j++) {
        const pdf = pickupOrderEmails[i].pdfs[j];
        const link = document.createElement('a');
        link.download = `${pdf.file_name}`;
        link.href = 'data:application/octet-stream;base64,' + pdf.data;
        link.click();
      }
    }
  };

  renderButtons = (pickupOrder: PickupOrderInvoice) => {
    if (pickupOrder.status == PickupOrderStatusManualActionRequired) {
      return html`
        <div class="row centered">
          <h2>Foutmelding:</h2>
        </div>
        <div class="row centered">
          <div>${pickupOrder.processing_error_msg}</div>
        </div>
        <div class="row centered">
          <langhout-button
            text="Opnieuw Afboeken"
            @click="${async () => {
              if (!this.hasFeedbackExactForAll()) {
                confirmPopup({
                  confirm_event: 'confirm_booking_retry',
                  title: 'Opnieuw afboeken bevestigen',
                  description:
                    'Niet alle boekingen zijn teruggekomen uit "automatisch afboeken" van Exact. Weet u zeker dat u opnieuw wilt afboeken? Dit kan resulteren in dubbele afboekingen.',
                  with_checkbox: true,
                  checkbox_text: '',
                });
                return;
              }
              this.disableButtons = true;
              await this.retryBooking();
              this.disableButtons = false;
            }}"
          ></langhout-button>
        </div>
        <div class="row centered">
          <h2>Afboeken handmatig verwerkt</h2>
        </div>
        ${pickupOrder.sub_orders.map(subOrder => {
          if (subOrder.booking_status === BookingStatusCompleted) {
            return html``;
          }
          return html`
            <div class="row label">
              ${subOrder.order_id} - ${subOrder.shipping_id}
            </div>
            <div class="row delivery-note-number">
              <div class="label">Pakbonnummer:</div>
              <div class="fill"></div>
              <input
                type="number"
                id="${subOrder.id}"
                class="deliveryNoteNumber"
              />
            </div>
          `;
        })}
        <div class="row centered">
          <langhout-button
            text="Afboeken handmatig verwerkt"
            @click="${async () => {
              this.disableButtons = true;
              await this.handleManualBooking(pickupOrder);
              this.disableButtons = false;
            }}"
          ></langhout-button>
        </div>
      `;
    }

    if (pickupOrder.status == PickupOrderStatusPackageReceiptCreated) {
      return html`
        <div class="row centered">
          <langhout-button
            text="Pakbon verzenden"
            @click="${async () => {
              this.disableButtons = true;
              await this.sendEmail();
              this.disableButtons = false;
            }}"
          ></langhout-button>
        </div>
      `;
    }

    return html``;
  };

  handleManualBooking = async (pickupOrder: PickupOrderInvoice) => {
    if (!this.shadowRoot) {
      return;
    }
    const deliveryNoteNumbers = this.shadowRoot.querySelectorAll(
      '.deliveryNoteNumber',
    );
    const manualProcessPayload = [];
    for (let i = 0; i < deliveryNoteNumbers.length; i++) {
      const deliveryNoteNumberInput = <HTMLInputElement>deliveryNoteNumbers[i];
      if (deliveryNoteNumberInput.value === '') {
        showNotification('delivery note number cannot be empty');
        return;
      }
      const deliveryNoteNumber = parseInt(deliveryNoteNumberInput.value);
      if (isNaN(deliveryNoteNumber)) {
        showNotification(
          `delivery note number ${deliveryNoteNumberInput.value} is incorrect`,
        );
        return;
      }
      manualProcessPayload.push({
        pickup_sub_order_id: deliveryNoteNumberInput.id,
        delivery_note_number: deliveryNoteNumber,
      });
    }
    const path = `/pickuporders/${pickupOrder.id}/book-manually`;
    const result = await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify(manualProcessPayload),
    );
    if (isSuccessfulAPIResponse(result)) {
      if (result.body.success) {
        showNotification('Manual booking is being processed');
        this.dispatchCloseEvent();
      }
    }
  };

  hasFeedbackExactForAll = (): boolean => {
    let hasForAll = true;
    for (let i = 0; i < this.currentInvoiceView.sub_orders.length; i++) {
      hasForAll =
        hasForAll &&
        this.currentInvoiceView.sub_orders[i].received_booking_feedback_exact;
    }

    return hasForAll;
  };

  retryBooking = async () => {
    const body = {
      status: PickupOrderStatusLoadingDone,
      ignore_stock: true,
    };
    this.updateStatus(this.currentInvoiceView, body, 'Order opnieuw afboeken');
  };

  sendEmail = async () => {
    const path = `/emails/${this.currentInvoiceView.id}`;
    const result = await authenticatedAPICall(path, 'POST', new Headers());
    if (isSuccessfulAPIResponse(result)) {
      if (result.body.success) {
        showNotification('Email(s) have been sent');
        this.dispatchCloseEvent();
      }
    }
  };

  getWeightText = (amount: number, weight: number): string => {
    const weightTotal = amount * weight;
    return `${weightTotal.toLocaleString('nl-NL')} kg`;
  };

  getWeightTotalText = (products: Product[]): string => {
    let totalWeight = 0;
    products.forEach((p: Product) => {
      totalWeight += p.amount * p.weight;
    });
    return `${Math.ceil(totalWeight).toLocaleString('nl-NL')} kg`;
  };

  getAmountText = (amount: number, type: number): string => {
    if (type === 2) {
      return '';
    }
    if (amount === 1) {
      return `(${amount} pak)`;
    }
    return `(${amount} pakken)`;
  };

  renderBeingLoadedTime = (pickupOrder: PickupOrderInvoice) => {
    if (pickupOrder.being_loaded_time === '') {
      return html``;
    }
    const time = new Date(pickupOrder.being_loaded_time);
    if (time.getFullYear() < 2020) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Geladen om:</div>
        <div class="value">
          ${formatTime(this.currentInvoiceView.being_loaded_time, false)}
        </div>
      </div>
    `;
  };

  renderReadyForPickupTime = (pickupOrder: PickupOrderInvoice) => {
    if (pickupOrder.ready_for_pickup_time === '') {
      return html``;
    }
    const time = new Date(pickupOrder.ready_for_pickup_time);
    if (time.getFullYear() < 2020) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Klaargezet om:</div>
        <div class="value">
          ${formatTime(this.currentInvoiceView.ready_for_pickup_time, false)}
        </div>
      </div>
    `;
  };

  renderCleanupTime = (pickupOrder: PickupOrderInvoice) => {
    if (pickupOrder.cleanup_time === '') {
      return html``;
    }
    const time = new Date(pickupOrder.cleanup_time);
    if (time.getFullYear() < 2020) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Opgeruimd om:</div>
        <div class="value">
          ${formatTime(this.currentInvoiceView.cleanup_time, false)}
        </div>
      </div>
    `;
  };

  renderInitUser = (pickupOrder: PickupOrderInvoice) => {
    if (pickupOrder.init_user === '' || !pickupOrder.init_user) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Klaargezet door:</div>
        <div class="value">${this.currentInvoiceView.init_user}</div>
      </div>
    `;
  };

  renderLoadingUser = (pickupOrder: PickupOrderInvoice) => {
    if (pickupOrder.loading_user === '' || !pickupOrder.loading_user) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Geladen door:</div>
        <div class="value">${this.currentInvoiceView.loading_user}</div>
      </div>
    `;
  };

  renderCleanupUser = (pickupOrder: PickupOrderInvoice) => {
    if (pickupOrder.cleanup_user === '' || !pickupOrder.cleanup_user) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Opgeruimd door:</div>
        <div class="value">${this.currentInvoiceView.cleanup_user}</div>
      </div>
    `;
  };

  renderTransporter = (pickupOrder: PickupOrderInvoice) => {
    if (pickupOrder.transporter === '') {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Transporteur:</div>
        <div class="value">${this.currentInvoiceView.transporter}</div>
      </div>
    `;
  };

  renderLicensePlate = (pickupOrder: PickupOrderInvoice) => {
    if (pickupOrder.license_plate === '') {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Kenteken:</div>
        <div class="value">${this.currentInvoiceView.license_plate}</div>
      </div>
    `;
  };

  updateStatus = async (
    pickupOrder: PickupOrderInvoice,
    statusPayload: Record<string, unknown>,
    message: string,
  ) => {
    const path = `/pickuporders/${pickupOrder.id}/status`;
    const result = await authenticatedAPICall(
      path,
      'PUT',
      new Headers(),
      JSON.stringify(statusPayload),
    );
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      showNotification(message);
      this.dispatchReloadEvent();
      this.scrollTo(0, 0);
    }
  };

  dispatchReloadEvent = () => {
    const event = new CustomEvent('reload', {
      detail: {},
    });
    this.dispatchEvent(event);
  };

  dispatchCloseEvent = () => {
    const event = new CustomEvent('close', {
      detail: {},
    });
    this.dispatchEvent(event);
  };

  addComment = () => {
    const message = this.commentInput ? this.commentInput.value : '';
    if (message === '') {
      showNotification('Opmerking kan niet leeg zijn');
      return;
    }
    this.addCommentCall(this.currentInvoiceView, message);
  };

  addCommentCall = async (pickupOrder: PickupOrderInvoice, message: string) => {
    const commentPayload = {
      message: message,
      type: 'portal',
    };
    const path = `/pickuporders/${pickupOrder.id}/comment`;
    const result = await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify(commentPayload),
    );

    if (isSuccessfulAPIResponse(result) && result.body.success) {
      showNotification('Opmerking toegevoegd');
      if (this.commentInput) {
        this.commentInput.value = '';
      }
      this.dispatchReloadEvent();
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-view-pickup-order-invoice': ViewPickupOrderInvoice;
  }
}
